// Central Phosphor icon registry. The redesigned pages reference icons by a
// short string name (mirroring the mock's `ph-*` classes) so row/detail code
// stays declarative; importing the specific components here keeps tree-shaking
// working instead of pulling the whole icon set.
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
  Archive as Bucket,
  ArrowLeft,
  ArrowsClockwise,
  ArrowSquareOut,
  Bell,
  BookOpenText,
  CaretDown,
  CaretUp,
  CaretUpDown,
  Check,
  Circle,
  CircleNotch,
  Clock,
  Cloud,
  Copy,
  Cube,
  Database,
  Export,
  FileMagnifyingGlass,
  Function as FunctionIcon,
  GitBranch,
  GlobeHemisphereWest,
  Link as LinkIcon,
  ListDashes,
  LockSimple,
  MagnifyingGlass,
  Plus,
  Play,
  RocketLaunch,
  ShieldCheck,
  SignOut,
  SquaresFour,
  Stack,
  Timer,
  Trash,
  UploadSimple,
  UserPlus,
  GearSix,
  DownloadSimple,
  Key,
  GitCommit,
  ArrowCounterClockwise,
  UsersThree,
  ShareNetwork,
  Warning,
  X,
  GithubLogo,
  IdentificationCard,
  PlugsConnected,
  LinkBreak,
  PencilSimple,
  Eye,
  EyeSlash,
  ArrowsMerge,
  TreeStructure,
  UserMinus,
  Pulse,
  Crown,
} from '@phosphor-icons/react';

export type IconName =
  | 'magnifying-glass'
  | 'caret-up-down'
  | 'caret-down'
  | 'caret-up'
  | 'cloud'
  | 'squares-four'
  | 'book-open-text'
  | 'bell'
  | 'cube'
  | 'stack'
  | 'globe'
  | 'function'
  | 'database'
  | 'bucket'
  | 'lock'
  | 'clock'
  | 'timer'
  | 'rocket'
  | 'plan'
  | 'warning'
  | 'plus'
  | 'upload'
  | 'trash'
  | 'refresh'
  | 'list'
  | 'export'
  | 'git-branch'
  | 'copy'
  | 'check'
  | 'x'
  | 'circle'
  | 'spinner'
  | 'play'
  | 'arrow-left'
  | 'link'
  | 'launch'
  | 'shield-check'
  | 'logout'
  | 'users'
  | 'user-plus'
  | 'share'
  | 'gear'
  | 'download'
  | 'key'
  | 'git-commit'
  | 'undo'
  | 'github'
  | 'identification'
  | 'plugs'
  | 'link-break'
  | 'pencil'
  | 'eye'
  | 'eye-slash'
  | 'merge'
  | 'tree'
  | 'user-minus'
  | 'pulse'
  | 'crown';

const REGISTRY: Record<IconName, PhosphorIcon> = {
  'magnifying-glass': MagnifyingGlass,
  'caret-up-down': CaretUpDown,
  'caret-down': CaretDown,
  'caret-up': CaretUp,
  cloud: Cloud,
  'squares-four': SquaresFour,
  'book-open-text': BookOpenText,
  bell: Bell,
  cube: Cube,
  stack: Stack,
  globe: GlobeHemisphereWest,
  function: FunctionIcon,
  database: Database,
  bucket: Bucket,
  lock: LockSimple,
  clock: Clock,
  timer: Timer,
  rocket: RocketLaunch,
  plan: FileMagnifyingGlass,
  warning: Warning,
  plus: Plus,
  upload: UploadSimple,
  trash: Trash,
  refresh: ArrowsClockwise,
  list: ListDashes,
  export: Export,
  'git-branch': GitBranch,
  copy: Copy,
  check: Check,
  x: X,
  circle: Circle,
  spinner: CircleNotch,
  play: Play,
  'arrow-left': ArrowLeft,
  link: LinkIcon,
  launch: ArrowSquareOut,
  'shield-check': ShieldCheck,
  logout: SignOut,
  users: UsersThree,
  'user-plus': UserPlus,
  share: ShareNetwork,
  gear: GearSix,
  download: DownloadSimple,
  key: Key,
  'git-commit': GitCommit,
  undo: ArrowCounterClockwise,
  github: GithubLogo,
  identification: IdentificationCard,
  plugs: PlugsConnected,
  'link-break': LinkBreak,
  pencil: PencilSimple,
  eye: Eye,
  'eye-slash': EyeSlash,
  merge: ArrowsMerge,
  tree: TreeStructure,
  'user-minus': UserMinus,
  pulse: Pulse,
  crown: Crown,
};

export type IconProps = {
  name: IconName;
  size?: number;
  color?: string;
  weight?: 'regular' | 'bold' | 'fill';
  spin?: boolean;
  style?: React.CSSProperties;
  title?: string;
};

export const Icon = ({
  name,
  size = 16,
  color,
  weight = 'regular',
  spin = false,
  style,
  title,
}: IconProps) => {
  const Cmp = REGISTRY[name];
  return (
    <Cmp
      size={size}
      color={color}
      weight={weight}
      className={spin ? 'ntt-spin' : undefined}
      style={{ flex: 'none', ...style }}
      aria-label={title}
    />
  );
};
